/**
 * 큐베이스 점검(쓰기 동결·DB 전환) 알림 통로 — 응답에서 점검을 알아채고, 화면 위 띠(MaintenanceBanner)에 알린다.
 *
 * 왜 필요한가: QBase 는 DB 를 옮기는 동안 조회만 받고 쓰기를 거절하며, 마지막에 한 번 재기동한다(15~30초 연결 불가).
 * 이것을 모르면 화면은 저장 실패를 '알 수 없는 오류'로, 재기동 틈을 '로그인 끊김'으로 보여 준다.
 *
 * 알아채는 신호 (QBase 약속 — BE1 도 같은 모양으로 넘긴다, backend services/qbase_maintenance):
 *   - 응답 헤더 `X-QBase-Maintenance: <phase>` (WRITE_FREEZE | SWITCH_PENDING | SWITCHING)
 *   - 503 본문 `resultCode: "MAINTENANCE"` (+ `maintenance:{...}`), 또는 문구가 `[MAINTENANCE]` 로 시작
 *   - 직전 단계가 DB 전환인데 연결 불가·502·504 → '전환 중, 곧 돌아온다'
 *
 * 점검 중이면 `retryAfterSec`(기본 30초)마다 상태 API 를 묻고, 끝났다고 하면 띠를 내린다.
 * **점검은 로그아웃이 아니다** — 이 통로는 401 알림(sessionExpiry)과 따로다.
 * **막힌 저장을 다시 보내지 않는다** — 화면의 입력은 그대로 두고 사용자가 점검 뒤 다시 누른다.
 *
 * 스토어를 직접 부르지 않는다 — services ↔ store 순환 import 를 만들지 않기 위해서다(sessionExpiry 와 같은 판단).
 */
import { QBASE_API } from '../config/qbase';

export interface MaintenanceInfo {
  active: boolean;
  readOnly?: boolean;
  phase?: string;
  reason?: string;
  runId?: string;
  message?: string;
  since?: string;
  expiresAt?: string;
  retryAfterSec?: number;
  /** QBase 에 닿지 못하는 중(전환 재기동 틈) */
  unreachable?: boolean;
}

export const MAINT_HEADER = 'X-QBase-Maintenance';
const MSG_PREFIX = '[MAINTENANCE]';
/** 연결이 끊겼을 때 '재기동 중'으로 읽는 단계 — 폴링 사이에 SWITCHING 을 못 보고 재기동으로 넘어갈 수 있어 PENDING 도 */
const SWITCH_PHASES = ['SWITCH_PENDING', 'SWITCHING'];
const DEFAULT_RETRY_SEC = 30;

type Listener = (info: MaintenanceInfo | null) => void;

let info: MaintenanceInfo | null = null;
const listeners = new Set<Listener>();
let timer: number | null = null;
let lastPoll = 0;
let polling = false;

export function getMaintenance(): MaintenanceInfo | null {
  return info;
}

export function subscribeMaintenance(fn: Listener): () => void {
  listeners.add(fn);
  return () => { listeners.delete(fn); };
}

function set(next: MaintenanceInfo | null): void {
  info = next;
  listeners.forEach((f) => { try { f(info); } catch { /* 듣는 쪽 오류가 통로를 막지 않게 */ } });
  schedule();
}

function update(partial: Partial<MaintenanceInfo>): void {
  const clean = Object.fromEntries(Object.entries(partial).filter(([, v]) => v !== undefined && v !== null && v !== ''));
  set({ ...(info || {}), ...clean, active: true });
}

function stripPrefix(msg: unknown): string | undefined {
  if (typeof msg !== 'string') return undefined;
  const s = msg.trim();
  return s.startsWith(MSG_PREFIX) ? s.slice(MSG_PREFIX.length).trim() : s;
}

/** 본문이 '점검 때문에 막혔다' 인가 — QBase(resultCode) · BE1(errorType) · 일부 QBase 컨트롤러(500 + [MAINTENANCE]) */
export function isMaintenanceBody(body: any): boolean {
  if (!body || typeof body !== 'object') return false;
  if (body.resultCode === 'MAINTENANCE' || body.errorType === 'qbase.maintenance') return true;
  const msg = body.resultMessage ?? body.message ?? body.detail;
  return typeof msg === 'string' && msg.trim().startsWith(MSG_PREFIX);
}

/**
 * 응답을 본다(본문은 이미 읽었으면 함께). 점검 때문에 **실패한** 응답이면 true —
 * 부르는 쪽은 이때 로그아웃·재시도로 가지 않는다. 성공 응답의 헤더도 여기서 기억한다.
 */
export function noteResponse(res: Response, body?: any): boolean {
  let phase: string | null = null;
  try { phase = res.headers?.get?.(MAINT_HEADER) || null; } catch { phase = null; }
  if (body !== undefined && res.status >= 500 && isMaintenanceBody(body)) {
    const m = (body && typeof body.maintenance === 'object' && body.maintenance) || {};
    update({
      readOnly: true,
      ...m,
      phase: m.phase || phase || info?.phase || 'WRITE_FREEZE',
      message: m.message || stripPrefix(body.resultMessage) || info?.message,
      unreachable: false,
    });
    return true;
  }
  if (phase) {
    const known = !!info?.active && info.phase === phase && !info.unreachable && !!info.message;
    update({ phase, readOnly: true, unreachable: false });
    if (!known) refreshSoon();            // 헤더에는 단계만 있다 — 문구·해제 시각은 상태 API 에서
    return res.status === 503;
  }
  if (res.status === 502 || res.status === 504) return noteNetworkError();
  return false;
}

/** 서버에 닿지 못했다 — 직전 단계가 DB 전환이면 '전환 중'으로 보고 true (로그아웃·오류 대신 기다리게) */
export function noteNetworkError(): boolean {
  if (info?.active && SWITCH_PHASES.includes(info.phase || '')) {
    update({ phase: 'SWITCHING', unreachable: true });
    return true;
  }
  return false;
}

/** 지금 '조회만 되는' 상태인가 — 저장 버튼 문구 등에서 쓸 수 있게 */
export function isReadOnlyNow(): boolean {
  return !!info?.active;
}

// ---------------------------------------------------------------- 상태 API 폴링

function schedule(): void {
  if (timer !== null) { window.clearTimeout(timer); timer = null; }
  if (!info?.active || !QBASE_API) return;
  const sec = Number(info.retryAfterSec) > 0 ? Number(info.retryAfterSec) : DEFAULT_RETRY_SEC;
  timer = window.setTimeout(() => { void checkMaintenance(); }, sec * 1000);
}

function refreshSoon(): void {
  if (Date.now() - lastPoll > 5000) void checkMaintenance();
}

/** 상태 API(GET /qbase/api/maintenance, 로그인 불필요)를 한 번 묻는다. 앱 시작 때와 점검 중 주기적으로 */
export async function checkMaintenance(): Promise<void> {
  if (!QBASE_API || polling) return;
  polling = true;
  lastPoll = Date.now();
  try {
    const res = await fetch(`${QBASE_API}/qbase/api/maintenance`, { credentials: 'include' });
    if (res.status === 404) { if (info) set(null); return; }      // 점검 기능이 없는 QBase
    if (!res.ok) {
      if (!noteResponse(res) && info) schedule();
      return;
    }
    const body = await res.json().catch(() => null);
    const m = body?.maintenance;
    if (m && m.active) set({ ...m, active: true, unreachable: false });
    else if (info) set(null);
  } catch {
    if (!noteNetworkError() && info) schedule();                 // 못 닿았다 — 점검 중이면 다음 차례에 다시
  } finally {
    polling = false;
  }
}
